import { lightenColor } from "../../../../utils/colors.ts"
import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { readAllDayDate } from "../../../../utils/date/get-date-safe.ts"
import { type CalendarEvent, type Event } from "../../types/types.ts"
import { fromUTC } from "../components/CalendarAddForms/time.ts"
import { NEUTRAL_EVENT_COLORS } from "../constants/event-colors.ts"

// A reminder carries no end, and FullCalendar would otherwise block out its
// default hour for it.
const OPEN_ENDED_MINUTES = 15

export const transformEventsForCalendar = (
  events: Event[]
): CalendarEvent[] => {
  if (!events || !Array.isArray(events)) return []

  return events.map((event) => {
    const type = event?.extended_props?.type || "event"
    // A per-row fallback gave two identical events different colours purely by
    // their position in the response.
    const theme = NEUTRAL_EVENT_COLORS
    const completed = event?.extended_props?.is_completed || false

    const backgroundColor = event?.background_color || theme?.background_color
    const borderColor = event?.border_color || theme?.border_color
    const textColor = event?.text_color || theme?.text_color

    const isAllDay = event?.all_day || false

    const allDay = type === "reminder" && isAllDay

    const start = isAllDay
      ? readAllDayDate(event?.start_at).format(DATE_FORMAT)
      : fromUTC(event?.start_at).format()

    let end: string | undefined
    if (event?.end_at) {
      end = isAllDay
        ? readAllDayDate(event.end_at, event.start_at)
            .add(1, "day")
            .format(DATE_FORMAT)
        : fromUTC(event.end_at).format()
    } else if (!isAllDay) {
      end = fromUTC(event?.start_at)
        .add(OPEN_ENDED_MINUTES, "minute")
        .format()
    }

    return {
      // id: event?.id,
      originalId: event?.id,
      title: event?.title,
      start,
      end,
      allDay,
      backgroundColor: completed
        ? lightenColor(backgroundColor, 65)
        : backgroundColor,
      borderColor: completed ? lightenColor(borderColor, 65) : borderColor,
      textColor: textColor,
      // Only timed meetings reschedule by drag: a reminder's synthetic display
      // end and an all-day meeting's `allDay: false` are presentation-only and
      // must never be persisted. The key must be absent for a draggable row —
      // even an explicit `undefined` is refined to `false` — so it defers to
      // the calendar-wide `editable` and the permission gate keeps working.
      ...((type !== "event" || isAllDay) && { startEditable: false }),

      extendedProps: {
        ...event,
        ...event?.extended_props,
        completed: event?.is_completed || false
      }
    } as any
  })
}
